import React, { useRef } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import clsx from 'clsx';
import EnrollmentStepSections from 'components/rtmEnrollment/EnrollmentStepSections';
import ProgressStepper from 'components/rtmEnrollment/ProgressStepper';
import type { EnrollmentStepConfig } from 'components/rtmEnrollment/enrollmentSteps';
import { getRtmEnrollmentStyles } from 'components/rtmEnrollment/rtmEnrollmentStyles';

type EnrollmentStepShellProps = {
  buttonText: string;
  currentStep: number;
  isBackDisabled: boolean;
  isPrimaryDisabled?: boolean;
  onBack: () => void;
  onPrimary: () => void;
  step: EnrollmentStepConfig;
  totalSteps: number;
};

export default function EnrollmentStepShell({
  buttonText,
  currentStep,
  isBackDisabled,
  isPrimaryDisabled = false,
  onBack,
  onPrimary,
  step,
  totalSteps,
}: EnrollmentStepShellProps) {
  const styles = getRtmEnrollmentStyles();
  const StepIcon = step.icon;
  const contentRef = useRef<HTMLDivElement>(null);

  function scrollToTop() {
    contentRef.current
      ?.closest('.populate-check-in-body')
      ?.scrollTo({ top: 0, behavior: 'auto' });
  }

  function handleBack() {
    scrollToTop();
    onBack();
  }

  function handlePrimary() {
    scrollToTop();
    onPrimary();
  }

  return (
    <div className={styles.layout.shell}>
      <div className={styles.layout.progressHeader}>
        <p className={styles.type.eyebrow}>
          JUST A QUICK READ - NOTHING TO FILL OUT
        </p>

        <ProgressStepper currentStep={currentStep} totalSteps={totalSteps} />
      </div>

      <div className={styles.layout.cardWrapper}>
        <div className={styles.layout.card}>
          <div className={clsx(styles.layout.cardInner, styles.layout.cardPadding)}>
            <div ref={contentRef} className={styles.layout.contentScroll}>
              <div className={styles.space.stepIntro}>
                <div className={styles.layout.stepIconContainer}>
                  <StepIcon className={styles.icon.step} aria-hidden="true" />
                </div>
                <div className={styles.space.headerStack}>
                  <h1 className={styles.type.title}>
                    {step.title}
                  </h1>
                </div>
              </div>

              <div className={clsx(styles.space.leadOffset, styles.type.lead)}>
                {step.lead}
              </div>

              <div className={styles.space.sectionsOffset}>
                <EnrollmentStepSections sections={step.sections} />
              </div>
            </div>

            <div className={styles.layout.footer}>
              <div className={styles.layout.footerInner}>
                <button
                  type="button"
                  disabled={isBackDisabled}
                  onClick={handleBack}
                  className={styles.actions.secondary}
                >
                  <ArrowBackIcon
                    className={styles.icon.secondaryArrow}
                    aria-hidden="true"
                  />
                  Back
                </button>
                <button
                  type="button"
                  disabled={isPrimaryDisabled}
                  onClick={handlePrimary}
                  className={styles.actions.primary}
                >
                  {buttonText}
                  <ArrowForwardIcon className={styles.icon.primaryArrow} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
